import React, { useEffect } from 'react'
import clsx from 'clsx'

import { hooks, I18n } from '@front/volcanion'

import { Loader, Route, Router, Routes, Outlet } from '@front/squirtle'

import { useHandlingIncident } from '@abra/hooks/useHandlingIncident'
import IvrBar from '@abra/elements/IVR'
import SideBar from '@abra/elements/sidebar'
import TopBar from '@abra/elements/topbar'

import Action from './Action'
import Authentication from './Authentication'
import AuthorizedPackage from './AuthorizedPackage'
import Billing from './Billing'
import Ceiling from './Ceiling/'
import CommercialFormula from './CommercialFormula'
import CommercialPackage from './CommercialPackage'
import Company from './Company'
import CompanyContract from './CompanyContract/'
import CompanyService from './CompanyService/'
import DailyActivity from './DailyActivity'
import Dispatch from './Dispatch'
import Driver from './Driver/'
import Equipment from './Equipment'
import Export from './Export'
import ExportLicense from './ExportLicense'
import ExportOrder from './ExportOrder'
import Group from './Group/'
import Home from './Home'
import Import from './Import'
import Incident from './Incident'
import IncidentHistory from './Incident/History/List'
import Job from './Job/'
import ImportList from './ImportList'
import Message from './Message'
import NoShow from './NoShow/'
import Order from './Order'
import Playground from './Playground'
import Poi from './Poi'
import PoiType from './PoiType'
import Pricerule from './Pricerule/'
import RateParameter from './RateParameter/'
import Schedule from './Schedule/'
import Setting from './Setting'
import SpecificParameter from './SpecificParameter/'
import Staff from './Staff'
import StaffHistory from './StaffHistory'
import Supervision from './Supervision'
import Taxicounter from './Taxicounter/'
import TelephonyProfile from './TelephonyProfile'
import TrafficAlert from './TrafficAlert'
import User from './User/'
import UserOption from './UserOption/'
import Vehicle from './Vehicle'
import Voucher from './Voucher'
import VehicleOption from './VehicleOption/'
import VehicleType from './VehicleType'

import './styles.scss'
import { Box, Button, Typography } from '@mui/material'
import { HookUtils } from '@front/volcanion/utils'
import LocalContextProvider from '@front/volcanion/contexts/local'
import { useCurrentAuthorization, useRoleAuthorizations } from '@front/volcanion/hooks'
import TitlePage from '@abra/elements/TitlePage'

function getRoleAuthorizations(authorized_pages, role_id) {
  return _.map(authorized_pages, (page) => {
    const authorization = _.find(page.authorizations, ['role', role_id])
    return {
      ..._.omit(page, ['authorizations']),
      ...authorization
    }
  })
}

const PageHandler = ({ suffix }) => {
  const navigate = hooks.useNavigate()
  return (
    <Box display={'flex'} flexDirection={'column'} flex={1} height={'20%'} alignItems={'center'} justifyContent={'center'}>
      <Box display={'flex'} flexDirection={'column'} flex={1} alignItems={'center'} justifyContent={'center'}>
        <Typography color={'secondary'} fontSize={24} fontWeight={'bold'}>{I18n.t(`page_${suffix}.title`)}</Typography>
        <Typography fontSize={20}>{I18n.t(`page_${suffix}.body`)}</Typography>
      </Box>
      <Button variant='contained' onClick={() => navigate('/')}>{I18n.t(`page_${suffix}.button`)}</Button>
    </Box>
  )
}

const RoleRouter = () => {
  const current_authorization = useCurrentAuthorization(true)
  const authorizations = useRoleAuthorizations(true)
  return !!current_authorization?.display ? <Outlet context={{ authorizations }} /> : <PageHandler suffix={'authorization'} />
}
const MemoRoleRouter = React.memo(RoleRouter)

const PrivateRouter = () => {
  useHandlingIncident()
  const VOUCHER_ENABLED = hooks.useRelayFeature('VOUCHER_ENABLED')
  return (
    <Routes>
      <Route path='*' element={<PageHandler suffix={'notfound'} />} />
      <Route path='/' element={<MemoRoleRouter />} >
        <Route path='/' exact element={<Home />} />
        <Route path='/adherent/*' element={<Group />} />
        <Route path='/authorizedpackage/*' element={<AuthorizedPackage />} />
        <Route path='/ceiling/*' element={<Ceiling />} />
        <Route path='/client/*' element={<User />} />
        <Route path='/commercialformula/*' element={<CommercialFormula />} />
        <Route path='/company/*' element={<Company />} />
        <Route path='/companycontract/*' element={<CompanyContract />} />
        <Route path='/companyservice/*' element={<CompanyService />} />
        <Route path='/daily-activity/*' element={<DailyActivity />} />
        <Route path='/dispatch/*' element={<Dispatch />} />
        <Route path='/driver/*' element={<Driver />} />
        <Route path='/driver-option/*' element={<UserOption />} />
        <Route path='/equipment/*' element={<Equipment />} />
        <Route path='/export/*' element={<Export />} />
        <Route path='/export/license/*' element={<ExportLicense />} />
        <Route path='/export/order/*' element={<ExportOrder />} />
        <Route path='/history/*' element={<Action />} />
        <Route path='/import' element={<Import />} />
        <Route path='/incident' element={<Incident />} />
        <Route path='/job/*' element={<Job />} />
        <Route path='/list/*' element={<ImportList />} />
        <Route path='/message/*' element={<Message />} />
        <Route path='/noshow/*' element={<NoShow />} />
        <Route path='/order/*' element={<Order />} />
        <Route path='/package/*' element={<CommercialPackage />} />
        <Route path='/playground/*' element={<Playground />} />
        <Route path='/poi/*' element={<Poi />} />
        <Route path='/poitype/*' element={<PoiType />} />
        <Route path='/pricerule/*' element={<Pricerule />} />
        <Route path='/price-rule/*' element={<Pricerule />} />
        <Route path='/rateparameter/*' element={<RateParameter />} />
        <Route path='/schedule/*' element={<Schedule />} />
        <Route path='/setting/*' element={<Setting />} />
        <Route path='/specificparameter/*' element={<SpecificParameter />} />
        <Route path='/staff/*' element={<Staff />} />
        <Route path='/staff-history/*' element={<StaffHistory />} />
        <Route path='/supervision/*' element={<Supervision />} />
        <Route path='/taxicounter/*' element={<Taxicounter />} />
        <Route path='/telephonyprofile/*' element={<TelephonyProfile />} />
        <Route path='/traffic-alert/*' element={<TrafficAlert />} />
        <Route path='/useroption/*' element={<UserOption />} />
        <Route path='/user-option/*' element={<UserOption />} />
        <Route path='/validation/*' element={<Billing />} />
        <Route path='/vehicle/*' element={<Vehicle />} />
        <Route path='/vehicle-option/*' element={<VehicleOption />} />
        <Route path='/vehicleoption/*' element={<VehicleOption />} />
        <Route path='/vehicletype/*' element={<VehicleType />} />
        {!!VOUCHER_ENABLED && <Route path='/voucher/*' element={<Voucher />} />}
        <Route path='/incident/history' element={<IncidentHistory />} />
      </Route>
    </Routes>
  )
}

const Title = () => {
  const current_page = hooks.useCurrentAuthorization(true)
  const current_page_name = current_page?.name_translated
  const incident_count = hooks.useSearchListenerCount('event', 'incident_count')
  const incident_count_label = incident_count > 0 ? `(${incident_count})` : ''
  return (
    <TitlePage
      current_page={current_page_name}
      extra={incident_count_label}
    />
  )
}

const withDrawer = (Component) => (props) => {
  const [isDrawerOpen] = hooks.useGlobalState('isDrawerOpen')
  const { CPP_URL } = hooks.useRelayConstants(['CPP_URL'])
  const { EXTERNAL_PHONE_SERVICE } = hooks.useRelayConstants(['EXTERNAL_PHONE_SERVICE'])
  const display_ivr = EXTERNAL_PHONE_SERVICE === 'aws'

  return <Component isDrawerOpen={isDrawerOpen} CPP_URL={CPP_URL} display_ivr={display_ivr} {...props} />
}
const PrivateRoute = ({ isDrawerOpen, CPP_URL, display_ivr }) => {

  const [authorized_pages, , search_state] = hooks.useModelSearch('page', 'getAuthorized', { default_limit: -1, initial_filter: {}, populate: ['authorizations'] })
  const [user, user_state] = hooks.useActiveUser({ populate: ['auth'] })
  const authorizations = getRoleAuthorizations(authorized_pages, user?.auth?.role)
  return (
    <LocalContextProvider context={{ authorizations }}>
      <Loader isLoading={HookUtils.getLoadingState([search_state, user_state])}>
        <Title />
        <TopBar>{!!display_ivr && !!CPP_URL && <IvrBar />}</TopBar>
        <SideBar />
        <div className={clsx('root', { ['shift']: isDrawerOpen })}>
          <PrivateRouter />
        </div>
      </Loader>
    </LocalContextProvider>
  )
}
const DrawerPrivateRoute = React.memo(withDrawer(PrivateRoute))

const withAuth = (Component) => (props) => {
  const isReady = hooks.useReadyStatus()
  const isAuthenticated = hooks.useAuthenticated()
  return <Component isReady={isReady} isAuthenticated={isAuthenticated} {...props} />
}

const Pages = ({ isReady, isAuthenticated }) => {
  const [, setMode] = hooks.useGlobalState('mode')

  // Force light mode on every reload — but gate on `isReady` so volcanion's
  // async rehydrateStorage has finished first. Without the gate, setMode raced
  // against rehydrate: ~50% of reloads the rehydrate overwrote our 'light' with
  // the stored value (often 'dark') after this effect ran.

  const toastEdges = isAuthenticated
    ? { top: 50, left: 55, right: 0, bottom: 0 }
    : { top: 0, left: 0, right: 0, bottom: 0 }

  useEffect(() => {
    if (isReady) setMode('light')
  }, [isReady])

  return (
    <Router toastEdges={toastEdges} toastPadding={16}>
      {!!isAuthenticated
        ? <DrawerPrivateRoute />
        : <Authentication />}
    </Router>
  )
}
const AuthPrivateRoute = React.memo(withAuth(Pages))

export default React.memo(AuthPrivateRoute)
